import { type HttpContext } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { BaseSidebarContainersItemComponent } from '@modules/common/components/base-sidebar-containers-item/base-sidebar-containers-item.component';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Application } from '@modules/common/types/application';
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { FoldersListStateService } from '@modules/files/services/folders-list-state.service';
import { FoldersService } from '@modules/files/services/folders.service';
import { File } from '@modules/files/types/file';
import { Folder } from '@modules/files/types/folder';
import { FoldersFilters } from '@modules/files/types/folders-filters';
import { VirtualFolder } from '@modules/files/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';
import { combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-side-bar-folders',
  templateUrl: './side-bar-folders.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-containers-item/base-sidebar-containers-item.component.less',
    './side-bar-folders.component.less',
  ],
  animations: [heightAnimation],
  standalone: false,
  providers: [
    ...FoldersListStateService.providers({
      stateKey: StateKey.sidebarFoldersListState,
    }),
  ],
})
export class SideBarFoldersComponent
  extends BaseSidebarContainersItemComponent<Folder, File, FoldersFilters, VirtualFolder>
  implements OnInit
{
  @Input() public title = 'Folders';
  @Input() public depth?: number;
  @Input() public tenantId: string | null | undefined;

  application = Application.files;
  sidebarSplitViewKey = SidebarSplitViewKey.files;

  private readonly drafts = new Map<string, Folder>();

  protected folderDraft(parentId?: string): Folder {
    const key = JSON.stringify([this.tenantId, parentId]);
    if (!this.drafts.has(key)) {
      this.drafts.set(key, new Folder({ accountId: this.tenantId, parentId }));
    }
    return this.drafts.get(key);
  }

  private readonly foldersListStateService: FoldersListStateService = inject(FoldersListStateService);
  private readonly foldersService = inject(FoldersService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  public ngOnInit(): void {
    super.ngOnInit();

    combineLatest([this.foldersListStateService.value(), this.displayTimeZoneService.resolvedTimeZone$])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.filters = new FoldersFilters({ displayTimeZone }).applyListState(state);
      });
  }

  protected override railRequestContext(): HttpContext | undefined {
    return this.tenantId === undefined ? undefined : mailboxTenantContext(this.tenantId);
  }

  public createFolder(folder: Folder): void {
    const requestContext = this.railRequestContext();
    this.foldersService
      .create(folder, { emit: true, toast: true, requestContext })
      .pipe(take(1), takeUntil(this.alive))
      .subscribe(() => this.quickFormPopoverClose.next());
  }
}
